import {
  StaysAttention,
  StaysErrorCode,
  StaysListing,
  StaysListingStatus,
  StaysPolicy,
  StaysUnitKind,
} from '../../../../interfaces/stays';
import { plural } from '../../../../function-utils/plural';

/**
 * LOBBERO STAYS — every word the staff screens say (plan S1).
 *
 * The server `message` of a Stays refusal IS the product copy (lobbero_be
 * src/stays/errors.js): the screens show it as sent. ERROR_COPY below is the
 * fallback for when a body carries no message, and its KEYS are checked against
 * the backend's code list by lobbero_be/scripts/stays/check-fe-copy.js — a code
 * added on one side only fails there.
 *
 * Lists (countries, currencies) mirror src/stays/vocabulary.js and are checked
 * against the shared case file (utils/stays-listing.cases.json) by
 * stays-labels.test.ts.
 */

export type PillTone = 'brand' | 'neutral' | 'success' | 'warning' | 'alert';

export const STATUS_LABEL: Record<StaysListingStatus, string> = {
  active: 'Live',
  paused: 'Paused',
  draft: 'Draft',
  retired: 'Retired',
};

export const STATUS_TONE: Record<StaysListingStatus, PillTone> = {
  active: 'success',
  paused: 'warning',
  draft: 'neutral',
  retired: 'neutral',
};

export const ATTENTION_LABEL = 'Needs attention';
export const ATTENTION_TONE: PillTone = 'alert';

const UNIT_STATUS_WORD: Record<string, string> = { offline: 'offline', archived: 'deleted', active: 'active' };

/** R-S1.21 — the attention banner's words. The kind sentence follows the
 *  platform policy (Q20), which reaches the FE in the entitlement response. */
export const attentionCopy = (
  reason: StaysAttention,
  listing: Pick<StaysListing, 'building_name' | 'unit_status'>,
  policy?: StaysPolicy
): string => {
  const building = listing.building_name || 'this building';
  switch (reason) {
    case 'unit_not_active':
      return `This unit is no longer active (${UNIT_STATUS_WORD[listing.unit_status] || listing.unit_status}). Guests can't book it.`;
    case 'unit_not_listable_kind':
      if (policy?.listable_unit_kinds === 'residential_only') {
        return "This unit is no longer marked residential. Guests can't book it.";
      }
      if (policy?.listable_unit_kinds === 'all_but_parking_storage') {
        return "This unit is now a parking or storage unit. Guests can't book it.";
      }

      return "This kind of unit can't be listed on Stays any more. Guests can't book it.";
    case 'unit_occupied':
      return "Someone now lives in this unit. Guests can't book it until they move out or you retire the listing.";
    case 'building_inactive':
      return `${building} is deactivated. Guests can't book it.`;
    case 'building_switch_off':
      return `Stays is switched off for ${building}. Switch it on to make this listing live.`;
    default:
      return "Guests can't book this listing right now.";
  }
};

export const KIND_LABEL: Record<StaysUnitKind, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
  common: 'Common area',
  parking: 'Parking',
  storage: 'Storage',
  unassigned: 'Unassigned',
};

/** Fallback words per code (the server's message wins when present). */
export const ERROR_COPY: Record<Exclude<StaysErrorCode, 'INVALID_FIELDS'>, string> = {
  STAYS_NOT_ENABLED: 'Lobbero Stays is not switched on for your organisation.',
  STAYS_BUILDING_NOT_FOUND: 'That building could not be found.',
  STAYS_UNIT_NOT_FOUND: 'That unit could not be found.',
  STAYS_LISTING_NOT_FOUND: 'That listing could not be found.',
  STAYS_BUILDING_INACTIVE: 'This building is deactivated.',
  STAYS_BUILDING_OFF: "Stays is switched off for this building. Switch it on in the building's settings first.",
  STAYS_UNIT_INACTIVE: 'This unit is not active. Only an active unit can be listed.',
  STAYS_UNIT_KIND: "This kind of unit can't be listed on Stays.",
  STAYS_UNIT_OCCUPIED: "Someone lives here. A unit with a resident can't be listed on Stays.",
  STAYS_LISTING_EXISTS: 'This unit is already on Stays.',
  STAYS_LISTING_RETIRED: 'This listing is retired. Promote the unit again to list it.',
  STAYS_LISTING_CHANGED: 'This listing changed since you opened it. It has been read again.',
  STAYS_TRANSITION: "That can't be done to this listing.",
  STAYS_RETIRE_BLOCKED: 'This listing has stays that are current or still to come. Cancel or move them first.',
  STAYS_BUILDING_HAS_ACTIVE: 'Pause or retire the live listings here first.',
  STAYS_RATE_LIMITED: 'Too many Stays changes. Please wait a few minutes.',
  UNIT_IS_STAYS_LISTING: 'This unit is on Stays. Retire its listing first.',
  STAYS_MOVE_IN_REFUSED: 'This unit is listed on Stays. Retire the listing before someone moves in.',
  STAYS_MOVE_IN_HAS_STAYS: 'This unit is listed on Stays and has stays still to come.',
  STAYS_FLAG_CONSOLE_OFF: 'Lobbero Stays is switched on and off by the Lobbero team for now, not from the console.',
  // Plan S3: stays (staff) and the desk.
  STAY_NOT_FOUND: 'That stay could not be found.',
  STAY_OVERLAP: 'These dates overlap another stay on this listing.',
  LISTING_NOT_ACTIVE: 'This listing is paused. Resume it to add stays.',
  STAY_LOCKED: "This stay can't be changed any more.",
  STAY_CHANGED: 'This stay changed since you opened it. It has been read again.',
  GUEST_VERIFIED: "The guest's name can't be changed once their ID is verified.",
  LINK_EXISTS: 'This stay already has a check-in link.',
  STAY_NOT_TODAY: 'Not expected today — sign them in as a normal visitor and ring the host.',
  STAY_OUTSIDE_WINDOW: 'Outside the expected arrival time. Tick the box to sign them in anyway.',
  STAY_CHECKIN_INCOMPLETE: 'Check-in is not finished online. Tick the box once you have checked their ID here.',
  ALREADY_SIGNED_IN: 'Already signed in.',
  // Plan S4: the full stay, owner blocks, the calendar.
  STAY_BLOCKED: 'Those nights are closed.',
  STAY_STATUS_NOT_ALLOWED: "That can't be done to this stay now.",
  STAY_TOO_EARLY: 'This stay has not started yet.',
  STAY_DATES_LOCKED: "These dates can't be changed now.",
  STAY_ACTIVE: 'This stay is still ahead. Cancel it first.',
  STAY_ARCHIVED: 'This stay is archived. Undo the archive first.',
  STAY_NOT_ARCHIVED: 'This stay is not archived.',
  STAY_NOT_DELETABLE: "This stay can't be deleted. Archive it instead.",
  STAY_NARROW_SEARCH: 'Too many stays to search. Narrow the search with dates.',
  LISTING_CURRENCY_LOCKED: "Amounts are recorded in this listing's currency, so it can't change.",
  BLOCK_NOT_FOUND: 'Those closed dates could not be found.',
  BLOCK_OVERLAP: 'These dates overlap dates that are already closed.',
  BLOCK_OVERLAPS_STAY: 'Part of these dates is booked. Choose dates without a stay.',
  BLOCK_PROVIDER_MANAGED: 'These dates come from a booking channel. Re-open them on the channel.',
  // Plan S6: the guest check-in, the link delivery, the house rules.
  NO_GUEST_EMAIL: 'This stay has no guest email. Add one, or copy the link instead.',
  RESEND_LIMIT: 'Sent several times recently. Try again a little later.',
  LINK_COMPLETED: 'The guest finished check-in. Use "Send again" to send the link anyway.',
  LINK_ACCESS_REVOKED: "The guest's access is revoked. Restore it to send the link.",
  LINK_STAY_CLOSED: 'This stay is not open for check-in.',
  LINK_NONE: 'This stay has no check-in link yet. Make one first.',
  STAYS_GUEST_NOT_FOUND: 'That guest could not be found.',
  IN_PERSON_NOT_ALLOWED:
    'You can confirm an ID in person only for an adult whose ID is not done yet, from 2 days before check-in.',
  ID_CHECK_NONE: 'This guest has not given their ID details yet.',
  ID_CHECK_DECIDED: 'This ID was already decided. The card has been read again.',
  GUEST_EVENT_NOT_FOUND: 'That message could not be found.',
  SIGNATURE_NOT_FOUND: 'That signed copy could not be found.',
  SIGNATURE_PDF_GONE: 'The signed copy was deleted at the end of its retention period.',
  RULES_EN_REQUIRED: 'Write the house rules and their summary before publishing.',
  RULES_NO_DRAFT: 'There is no draft to publish.',
  RULES_CHANGED_STAFF: 'The house rules changed since you opened them. They have been read again.',
  // Plan S7.
  STALE_GUIDE: 'Someone saved this guide a moment ago. Reload to see their changes.',
  STALE_ACCESS_DETAILS: 'Someone saved these access details a moment ago. Reload to see their changes.',
  CODE_IN_TEXT: 'This text contains a door code. Keep codes in Access details only.',
  FAQ_LIMIT: 'A guide can have up to 50 questions. Delete one to add another.',
  TIP_LIMIT: 'A guide can have up to 60 local tips. Delete one to add another.',
  GALLERY_LIMIT: 'The gallery can hold up to 40 photos. Delete one to add another.',
  FAQ_NOT_FOUND: 'That question could not be found.',
  TIP_NOT_FOUND: 'That tip could not be found.',
  MEDIA_NOT_FOUND: 'That photo could not be found.',
  MEDIA_NOT_STORED: 'Could not store the photo. Try again.',
  MEDIA_STORAGE_OFF: 'Image storage is not configured.',
  CODE_ALREADY_IN_TEXT: "This code also appears in the guide's text. Remove it there first, then save.",
  LIST_CHANGED: 'This list changed since you opened it. It has been read again.',
  // Plan S5: calendar sync.
  FEED_NOT_FOUND: 'This calendar is no longer connected.',
  FEED_URL_INVALID: 'Paste the full https link to the calendar.',
  FEED_HOST_MISMATCH: 'This is not that booking site’s calendar link.',
  FEED_PROVIDER_EXISTS: 'That booking site is already connected to this listing.',
  FEED_URL_IN_USE: 'This calendar is already connected.',
  FEED_FETCH_FAILED: 'The link could not be checked.',
  FEED_NOT_ICAL: 'The link did not return a calendar.',
  FEED_TOO_LARGE: 'The calendar is larger than 2 MB.',
  FEED_CHANGED: 'Someone else changed this calendar. Open it again.',
  FIELD_IMMUTABLE: 'The booking site of a connected calendar cannot be changed.',
  SYNC_TOO_SOON: 'This calendar was checked a moment ago. Try again in a minute.',
  REVIEW_NOT_FOUND: 'This change is no longer waiting for you.',
  REVIEW_CHANGED: 'This change was updated since you opened it. It has been read again.',
  RESERVATION_OVERLAP: 'These dates clash with another stay or closed dates.',
  EDIT_NOT_ALLOWED: 'This change cannot be made here.',
  EXPORT_NOT_FOUND: 'This link is already off.',
  // Plan S8.
  STAYS_SETTINGS_STALE: 'Someone else changed these settings. Reload to see their version.',
  TEMPLATE_PLACEHOLDER_UNKNOWN: 'The message uses a detail Lobbero cannot fill.',
  TEMPLATE_EMPTY: 'Write the message before saving.',
  TEMPLATE_TOO_LONG: 'The message is too long.',
  TEMPLATE_INVALID_CHARACTERS: 'The message has characters that cannot be sent, or too many lines.',
  SUMMARY_SLOTS_INVALID: 'Choose one to three different send times, in whole minutes.',
  ALERT_TEST_FAILED: 'The test could not be sent. Please try again.',
  ALERT_TEST_LIMITED: 'You have sent several tests. Please wait a few minutes before sending again.',
  SUMMARY_NOW_LIMITED: 'Please wait a few minutes before sending again.',
  CLEANING_TRANSITION_INVALID: 'The cleaning changed a moment ago. Reload to see it.',
  RESERVATION_NOT_LIVE: 'Cleaning cannot change on a cancelled or no-show stay.',
  TEMPLATE_NOT_FOUND: 'That template could not be found.',
  TEMPLATE_STALE: 'Someone else changed this template. Reload to see their version.',
  COMPOSE_LIMITED: 'Too many messages prepared at once. Please wait a minute.',
  // Independent Stays IS2 — properties.
  PROPERTIES_ARE_FOR_STAYS_ONLY: 'Your account has Lobbero Desk. Add buildings and units from Buildings, then list a unit on Stays.',
  PROPERTY_NOT_FOUND: 'That property could not be found.',
  ATTESTATION_REQUIRED: 'Tick the box to confirm you are allowed to rent this property for short stays.',
  PREVIEW_CAP_REACHED: 'You have used all the places to stay in your free preview.',
  PROPERTY_NOT_MULTI: 'Apartments can only be added to a property with several apartments.',
  PROPERTY_HAS_FUTURE_STAYS: 'This property has stays still to come. Cancel or move them first.',
  PROPERTY_OCCUPIED: 'Someone lives in an apartment of this property. Move them out first.',
  ZONE_CHANGE_HAS_STAYS: "This move changes the property's time zone, and it has stays still to come.",
  ZONE_CHANGE_NEEDS_CONFIRM: 'This moves the property to another time zone. Every time shown for it moves too.',
  // Independent Stays IS4 — Stays billing.
  NO_LISTING_SLOT: 'Every listing slot is in use. Add a slot on Stays → Billing, then make this listing live.',
  STAYS_BILLING_OFF: 'Billing for Lobbero Stays is not switched on yet.',
  STAYS_COMPED_ACCOUNT: 'This is a complimentary account. Billing changes are made by Lobbero.',
  STAYS_ADDON_ONLY: 'Listing slots are for accounts with Lobbero Desk. Your plan is on Billing.',
  LISTINGS_BELOW_USED: 'Some of these slots hold live listings. Retire a listing before you return its slot.',
  QUOTE_CHANGED: 'The amount changed while you were deciding. Check the new amount and confirm again.',
  STAYS_NO_PAYMENT_METHOD: 'Add a card on Billing first. Nothing was charged.',
  LISTINGS_STILL_LIVE: 'Retire every listing before you stop paying for Stays.',
  NOT_A_PREVIEW_ACCOUNT: 'This account already has a plan. Change it on Billing.',
  STAYS_BILLING_UNAVAILABLE: 'We could not reach our payment provider, so nothing was charged. Please try again in a minute.',
  PROPERTIES_BELOW_USED: 'You already have more places to stay than that. Choose at least as many as you have.',
  STAYS_CARD_NOT_CONFIRMED: 'The card was not confirmed, so nothing was charged. Please add it again.',
  STAYS_CARD_NOT_YOURS: 'That card confirmation does not belong to this account.',
  STAYS_PAYMENT_FAILED: 'The card was declined, so no plan was started. Try another card.',
  // Independent Stays IS5 — co-hosts and the team.
  COHOST_FIELD_NOT_ALLOWED: 'Co-hosts cannot change amounts, the currency or who hosts a listing. Ask the owner.',
  COHOST_NOT_ALLOWED: 'Co-hosts cannot do this. Ask the owner.',
  COMPLIANCE_NOT_AVAILABLE: 'Guest registration is not available yet.',
  COHOST_NOT_FOUND: 'That co-host could not be found.',
  TEAM_LISTING_NOT_ALLOWED: 'Choose listings that are live and that you manage.',
  CHECKIN_RESEND_LIMIT: 'This link was sent many times in the last hour. Try again later.',
  // Independent Stays IS9 — hotels, room types, stays waiting for a room.
  NOT_A_HOTEL: 'Room types belong to a hotel. This property is not a hotel.',
  ROOM_TYPE_NOT_FOUND: 'That room type could not be found.',
  ROOM_TYPE_NAME_TAKEN: 'This hotel already has a room type with that name. Give it another name.',
  ROOM_TYPE_CHANGED: 'This room type changed since you opened it. It has been read again.',
  ROOM_TYPE_HAS_ROOMS: 'This room type still has rooms. Move them to another type or remove them first.',
  ROOM_TYPE_FULL: 'Every room of this type is taken on one of these nights. Choose other dates or another room type.',
  ROOM_NEEDED: 'Give this stay a room first. A stay without a room cannot be checked in or out.',
  ROOM_TYPE_HAS_WAITING_STAYS: 'Stays waiting for a room are held on this room. Give them their rooms first, then change its type.',
  ROOM_NOT_OF_TYPE: 'That room is not of the stay’s room type.',
  STAY_HAS_ROOM: 'This stay already has its room.',
  STAY_NOT_HOTEL: 'This stay is not at a hotel.',
  ROOM_NOT_FREE: 'That room is not free for these nights. Choose another room.',
  // Independent Stays IS7 — the building link.
  BUILDING_LINK_CODE_INVALID: 'This code cannot be used. Ask the building for a new one.',
  BUILDING_LINK_EXISTS: 'This listing already has a building link. End it first.',
  BUILDING_LINK_NOT_FOUND: 'That building link could not be found.',
  BUILDING_LINK_NOT_WAITING: 'This request has already been decided.',
  BUILDING_LINK_UNIT_INVALID: 'Choose an active unit in this building.',
  BUILDING_LINK_UNIT_TAKEN: 'That unit is already linked to a host. End that link first.',
  BUILDING_LINK_LISTING_RETIRED: 'A retired listing cannot be linked.',
  BUILDING_LINK_RATE_LIMITED: 'Too many tries. Please wait a little and try again.',
};

/** The words a refusal shows: the server's own message for a Stays code,
 *  else the fallback for the code, else the caller's generic sentence. Never
 *  raw axios text. */
export const refusalText = (
  err: { code?: string | null; message?: string; status?: number } | null | undefined,
  generic: string
): string => {
  if (!err) return generic;
  const code = err.code as keyof typeof ERROR_COPY | undefined;
  if (code && ERROR_COPY[code]) {
    return err.message && !/^Request failed|Network Error/i.test(err.message) ? err.message : ERROR_COPY[code];
  }

  return generic;
};

/** "Pause or retire the 2 live listings here first." */
export const hasActiveText = (count: number): string =>
  `Pause or retire the ${count} live ${plural(count, 'listing')} here first.`;

/* ── Countries and currencies (mirror of src/stays/vocabulary.js) ───────── */
export const STAYS_COUNTRIES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'PT', name: 'Portugal' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'DE', name: 'Germany' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'GR', name: 'Greece' },
  { code: 'HR', name: 'Croatia' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'SE', name: 'Sweden' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AE', name: 'United Arab Emirates' },
];

export const STAYS_CURRENCIES: ReadonlyArray<string> = ['EUR', 'GBP', 'CHF', 'SEK', 'MKD', 'USD', 'CAD', 'AED'];

export const COUNTRY_CURRENCY: Readonly<Record<string, string>> = {
  PT: 'EUR', ES: 'EUR', IT: 'EUR', FR: 'EUR', IE: 'EUR', NL: 'EUR', BE: 'EUR', DE: 'EUR', AT: 'EUR',
  GR: 'EUR', HR: 'EUR', GB: 'GBP', CH: 'CHF', SE: 'SEK', MK: 'MKD', US: 'USD', CA: 'CAD', AE: 'AED',
};

export const countryName = (code: string): string =>
  (STAYS_COUNTRIES.find(c => c.code === code) || { name: code }).name;

/** "Up to 4" */
export const guestsText = (n: number): string => `Up to ${n}`;

/** "Unit 12B · Koktower"; without Desk (Independent Stays IS2) "Flores Loft · 12B" — no unit word. */
export const unitLine = (
  l: { unit_number: string; building_name: string; unit_floor?: string | null },
  desk = true
): string =>
  (desk ? [`Unit ${l.unit_number}`, l.building_name] : [l.building_name, l.unit_number])
    .concat(l.unit_floor ? [`Floor ${l.unit_floor}`] : [])
    .filter(Boolean)
    .join(' · ');
